import React from "react";
import VersionText from "../../../../components/atoms/VersionText";
import {
  pathogenicStrongColor,
  benignStrongColor,
  defaultPathogenicityColor,
  uncertainSignificanceColor,
} from "../../../../common/helpers/colors";
import { cropNumber } from "../../../../common/helpers/helpers";
import * as constants from "./InSilicoPredictionsConstants";
import * as urls from "./InSilicoPredictionsUrls";
import HelperText from "../../../../components/atoms/HelperText";

export const checkIfResultIsEmpty = (dbnsfp) => {
  let i = 0;
  let found = false;
  const increaseBy = 1;

  while (constants.DBNSFP_PREDICTIONS_FIELDS.length > i && found === false) {
    if (dbnsfp[constants.DBNSFP_PREDICTIONS_FIELDS[i]] != null) {
      found = true;
    }
    i += increaseBy;
  }
  return found;
};

export const getDbnsfpVersion = (dbnsfp) => {
  if (dbnsfp) {
    const { version } = dbnsfp;
    const url = urls.dbnsfpUrl;
    return (
      <VersionText anchorUrl={url} anchorText="dbNSFP" versionText={version} />
    );
  }
  return null;
};

export const getDbnsfpPremiumVersion = (dbnsfpPremium) => {
  if (dbnsfpPremium) {
    const { version } = dbnsfpPremium;
    const url = urls.dbnsfpUrl;
    return (
      <VersionText anchorUrl={url} anchorText="dbNSFP" versionText={version} />
    );
  }
  return null;
};

export const getColor = (name, score, thresholds) => {
  const databaseName = name === "mutpred" ? "mutPred" : name;
  const databaseScore = databaseName === "sift" ? -score : score;
  if (
    !thresholds ||
    !thresholds[`${databaseName}_pathogenic`] ||
    !thresholds[`${databaseName}_benign`]
  ) {
    return defaultPathogenicityColor;
  }
  if (databaseScore >= thresholds[`${databaseName}_pathogenic`]) {
    return pathogenicStrongColor;
  }
  if (databaseScore <= thresholds[`${databaseName}_benign`]) {
    return benignStrongColor;
  }
  return uncertainSignificanceColor;
};

/* Returns an array of predictions based on an array score.
   e.g. score = [null, null, 0.890, 0.125] or score = [0.125]
   predictionsArray = ["", "", "Pathogenic", "Benign"]  or predictionsArray = ["Benign"] */
export const getPredictionsArrayBasedOnScore = (name, score, thresholds) => {
  const predictionsArray = [];
  const databaseName = name === "mutpred" ? "mutPred" : name;

  if (Array.isArray(score) && score.length > 0) {
    score.forEach((value) => {
      if (value && value >= thresholds[`${databaseName}_pathogenic`]) {
        predictionsArray.push("Pathogenic");
      } else if (value && value <= thresholds[`${databaseName}_benign`]) {
        predictionsArray.push("Benign");
      } else if (
        value &&
        (thresholds[`${databaseName}_benign`] ||
          thresholds[`${databaseName}_pathogenic`])
      ) {
        predictionsArray.push("Uncertain");
      } else {
        predictionsArray.push("");
      }
    });
  }

  return predictionsArray.filter((pred) => pred !== "");
};

/* Returns a predictions based on a score.
   e.g. score = 0.125, prediction = "Benign" */
export const getPredictionBasedOnScore = (name, score, thresholds) => {
  let prediction;
  const databaseName = name === "mutpred" ? "mutPred" : name;

  if (score && !Array.isArray(score)) {
    if (score && score >= thresholds[`${databaseName}_pathogenic`]) {
      prediction = "Pathogenic";
    } else if (score && score <= thresholds[`${databaseName}_benign`]) {
      prediction = "Benign";
    } else if (score) {
      prediction = "Uncertain";
    } else {
      prediction = "";
    }
  }

  return prediction;
};

/* Returns prediction(s) value(s) ("Pathogenic", "Uncertain", "Benign")
   using thresholds and score. */
export const renderPredictionFromScore = (db, score, thresholds) => {
  if (!thresholds) {
    return <td />;
  }

  const predictions = getPredictionsArrayBasedOnScore(db, score, thresholds);
  const prediction = getPredictionBasedOnScore(db, score, thresholds);
  const filteredItems = predictions.filter((value) => value !== null);
  const preds =
    predictions && filteredItems.length > 0
      ? filteredItems.map((pred, i) => {
          if (pred === null) return null;
          const key = `${i}-pred`;
          return (
            <span
              style={{ color: constants.PREDICTIONS_COLORS[pred] }}
              key={key}
            >
              {pred}
              {i < filteredItems.length - 1 ? ", " : ""}
            </span>
          );
        })
      : null;

  const pred = prediction ? (
    <span style={{ color: constants.PREDICTIONS_COLORS[prediction] }}>
      {prediction}
    </span>
  ) : null;

  if (preds || pred) {
    return (
      <td>
        <div className="varsome-horiz-list-label varsome-dbnsfp-label">
          prediction
        </div>
        <div className="varsome-horiz-list-value">{preds || pred}</div>
      </td>
    );
  }

  return <td />;
};

/* Returns prediction value ("Pathogenic", "Uncertain", "Benign")
  to be used in sorting. */
export const getPredictionFromScore = (db, score, thresholds) => {
  if (!thresholds) {
    return false;
  }

  const predictionsArray = getPredictionsArrayBasedOnScore(
    db,
    score,
    thresholds
  );
  const prediction = getPredictionBasedOnScore(db, score, thresholds);

  return predictionsArray && predictionsArray.length > 0
    ? predictionsArray.find((element) => element === "Pathogenic") ||
        predictionsArray.find((element) => element === "Uncertain") ||
        predictionsArray.find((element) => element === "Benign")
    : prediction;
};

export const renderMultipleCommasWithDescription = (
  item,
  prop,
  label,
  crop = false,
  helpTextKey,
  splitLabel = true,
  color = false,
  thresholds = null,
  db = null
) => {
  if (item[prop] == null || item[prop] === []) {
    return <td />;
  }

  if (Array.isArray(item[prop]) && item[prop].length > 0) {
    const filteredItems = item[prop].filter((value) => value !== null);
    return (
      <td>
        <div className="varsome-horiz-list-label varsome-dbnsfp-label">
          {splitLabel && label.split(" ").slice(1).join(" ")}
          {!splitLabel && label}
          {helpTextKey && <HelperText fieldDescriptionKey={helpTextKey} />}
        </div>
        <div className="varsome-horiz-list-value">
          {filteredItems.length > 0 &&
            filteredItems.map((value, i) => {
              const c = color ? getColor(db, value, thresholds) : "";
              const key = `${i}-value`;
              return (
                <span key={key} style={{ color: c }}>
                  {crop ? cropNumber(value) : value}
                  {i < filteredItems.length - 1 ? <span>, </span> : ""}
                </span>
              );
            })}
        </div>
      </td>
    );
  }

  const c = color ? getColor(db, item[prop], thresholds) : "";
  return (
    <td>
      <div className="varsome-horiz-list-label varsome-dbnsfp-label">
        {splitLabel && label.split(" ").slice(1).join(" ")}
        {!splitLabel && label}
        {helpTextKey && <HelperText fieldDescriptionKey={helpTextKey} />}
      </div>
      <div className="varsome-horiz-list-value" style={{ color: c }}>
        {crop ? cropNumber(item[prop]) : item[prop]}
      </div>
    </td>
  );
};

/* Returns prediction value when there is item[pred] */
export const renderPredictions = (
  item,
  prop,
  label,
  predictionMap,
  helpTextKey
) => {
  if (Array.isArray(item[prop]) && item[prop].length > 0) {
    const filteredItems = item[prop].filter((value) => value !== null);
    return (
      <td>
        <div className="varsome-horiz-list-label varsome-dbnsfp-label">
          {label.split(" ").slice(1).join(" ")}
          {helpTextKey && <HelperText fieldDescriptionKey={helpTextKey} />}
        </div>
        {predictionMap && filteredItems.length > 0 ? (
          <div className="varsome-horiz-list-value">
            {filteredItems.length > 0 &&
              filteredItems.map((pred, i) => {
                const key = `${i}-prediction`;
                return (
                  <span
                    style={{
                      color: constants.PREDICTIONS_COLORS[predictionMap[pred]],
                    }}
                    key={key}
                  >
                    {predictionMap[pred]}
                    {i < filteredItems.length - 1 ? ", " : ""}
                  </span>
                );
              })}
          </div>
        ) : (
          <div className="varsome-horiz-list-value">
            {filteredItems.length > 0 &&
              filteredItems.map((pred, i) => {
                if (pred === null) return null;
                const key = `${i}-${pred}`;
                return (
                  <span
                    style={{ color: constants.PREDICTIONS_COLORS[pred] }}
                    key={key}
                  >
                    {pred}
                    {i < item[prop].length - 1 && item[prop][i + 1] !== null
                      ? ", "
                      : ""}
                  </span>
                );
              })}
          </div>
        )}
      </td>
    );
  }
  if (!Array.isArray(item[prop])) {
    return (
      <td>
        <div className="varsome-horiz-list-label varsome-dbnsfp-label">
          {label.split(" ").slice(1).join(" ")}
          {helpTextKey && <HelperText fieldDescriptionKey={helpTextKey} />}
        </div>
        {predictionMap ? (
          <div
            className="varsome-horiz-list-value"
            style={{
              color: constants.PREDICTIONS_COLORS[predictionMap[item[prop]]],
            }}
          >
            {predictionMap[item[prop]]}
          </div>
        ) : (
          <div
            className="varsome-horiz-list-value"
            style={{ color: constants.PREDICTIONS_COLORS[item[prop]] }}
          >
            {[item[prop]]}
          </div>
        )}
      </td>
    );
  }
  return <td />;
};
